import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChange,
  ViewChild,
} from '@angular/core';
import { Encoding } from '@mode-vegas/vegas';
import { CapraIconsRegistry } from '@mode/capra';
import {
  ChartDataErrorReason,
  FacetSerializer,
  MuzeChartMenu,
  MuzeChartRenderer,
  MuzeDataModel,
  MuzeFeatureFlags,
  NoDataError,
  notifyMuzeRenderError,
} from '@mode/charts/util-muze';
import { EncodingHelpers, RenderDataModel, computePalette } from '@mode/charts/util-vegas';
import { ChartTypes, ErrorReporter, InterActions } from '@mode/shared/contract-common';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { DEFAULT_CHART_FORMAT, pause } from '@mode/shared/util-js';
import { AnalyticsEvent } from '@mode/shared/util-segment';
import { isEqual } from 'lodash';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime, first } from 'rxjs/operators';
import * as VizRendererHelpers from './viz-renderer.helpers';

enum RENDER_TYPE {
  designer = 'designer',
  exploration_designer = 'exploration_designer',
  exploration_viewer = 'exploration_viewer',
  report_builder = 'report_builder',
  report_viewer = 'report_viewer',
}

enum CANVAS_STATE {
  uninitialized = 'uninitialized',
  initialized = 'initialized',
  beforeLayout = 'beforeLayout',
  afterLayout = 'afterLayout',
  beforeRendered = 'beforeRendered',
  afterRendered = 'afterRendered',
  beforeDisposed = 'beforeDisposed',
  afterDisposed = 'afterDisposed',
}

export interface Paging {
  isPaginated: boolean;
  currentPage: number;
  totalPageCount: number;
  totalFacetCount: number;
  facetCountForPage: number;
  pageSize: number;
}

// These are the limits where the rendering may take a while
const RENDER_DELAY_POINTS = MuzeChartRenderer.MAX_POINTS / 5;
const RENDER_DELAY_SERIES = MuzeChartRenderer.MAX_SERIES / 3;
const RENDER_DELAY_FACETS = MuzeChartRenderer.MAX_FACETS / 10;

// Facet page size limit
const MAX_FACETS_PER_PAGE = 4000;

@Component({
  selector: 'mode-viz-renderer',
  templateUrl: './viz-renderer.component.html',
  styleUrls: ['./viz-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VizRendererComponent implements OnChanges, OnDestroy {
  @Input() token!: string;
  @Input() renderContext?: RENDER_TYPE = RENDER_TYPE.report_viewer;
  @Input() renderModel!: RenderDataModel;
  @Input() fieldColors!: ChartTypes.FieldColorMap;
  @Input() facetSizes!: ChartTypes.FacetSizeMap;
  @Input() format!: ChartTypes.ChartFormat;
  @Input() fieldFormats!: ChartTypes.FieldFormatMap;
  @Input() encoding!: Encoding;
  @Input() isWhiteLabel?: boolean = false;
  @Input() isEmbedded?: boolean = false;
  @Input() drilldownEnabled?: boolean = false;
  @Input() preventExpandingInteraction?: boolean = false;
  @Input() isViewPage?: boolean = false;
  @Input() enableContextExploration?: boolean = false;
  @Input() chartType?: string = '';
  @Input() suspendRendering = false;
  @Input() isTemporalDrillAllowed = false;
  @Input() drillAnywhereEnabled = false;

  // TODO: Legacy service to migrate
  @Input() reportApiService?: any;

  @Output() embedAction = new EventEmitter<any>();
  @Output() chartAction = new EventEmitter<{ componentId: string; action: InterActions.Union }>();
  @Output() renderStart = new EventEmitter<boolean>();
  @Output() renderDelay = new EventEmitter();
  @Output() renderEnd = new EventEmitter<{ error?: Error; options?: any }>();
  @Output() updateFacetSizes = new EventEmitter<FacetSerializer>();
  @Output() dataLimitsExceeded = new EventEmitter<VizRendererHelpers.DataLimitsResults>();

  currentContextMenuX: number | undefined | null;
  currentContextMenuY: number | undefined | null;

  canvas?: MuzeChartRenderer.MuzeCanvas;

  chartContentResizeObserver?: ResizeObserver;
  resizeSubject = new Subject<void>();
  resizeSubscription!: Subscription | null;
  subscription!: Subscription | null | undefined;
  errorSubscription!: Subscription | null;

  isFooterNeeded = false;

  private renderPerformanceStart?: number;
  private muzePerformanceStart?: number;
  private renderTimings: Record<string, number> = {};
  private dataStats: MuzeChartRenderer.VisualizationDataStats | undefined;
  private visibilityChanges: Array<{ type: 'hidden' | 'visible'; timestamp: number }> = [];

  private isMuzeEvent = false; // true if received callback from Muze event
  private canvasState: CANVAS_STATE = CANVAS_STATE.uninitialized;
  private muzeVersion = '';

  private needsCanvasRefresh = false;

  private maxElementCount = MuzeChartRenderer.MAX_ELEMENT_COUNT;

  private maxFacetPageSize = MAX_FACETS_PER_PAGE;

  private createdDataModels: Array<MuzeDataModel | undefined> = [];

  // This observable maintains the state of pagination for faceted charts.
  // Because we update this state in a Muze callback, an observable change
  // will drive change detection through the component.
  paging$ = new BehaviorSubject<Paging>({
    isPaginated: false,
    currentPage: 0,
    totalPageCount: 0,
    totalFacetCount: 0,
    facetCountForPage: 0,
    pageSize: 1,
  });

  @ViewChild('chartContent') chartContent!: ElementRef;

  constructor(
    private ngZone: NgZone,
    private errorService: ErrorReporter,
    private featureFlagsFacade: FeatureFlagsFacade,
    private capraIconsRegistry: CapraIconsRegistry
  ) {
    this.resizeSubscription = this.resizeSubject
      .asObservable()
      .pipe(debounceTime(200))
      .subscribe(this.resize.bind(this));
    // Max element data limit from Launch Darkly
    this.featureFlagsFacade
      .featureValueAsObservable<number>(FeatureFlag.VizExplorerMaxElementCount)
      .pipe(first())
      .subscribe((maxElements) => (this.maxElementCount = maxElements));
    // Facet paging size limit
    this.featureFlagsFacade
      .featureValueAsObservable<number>(FeatureFlag.VizExplorerMaxFacetsPerPage)
      .pipe(first())
      .subscribe((maxFacets) => (this.maxFacetPageSize = maxFacets > 0 ? maxFacets : MAX_FACETS_PER_PAGE));
  }

  @HostListener('window:resize')
  handleWindowResize() {
    this.resizeSubject.next();
  }

  @HostListener('document:visibilitychange', ['$event'])
  handleDocumentVisibilityChange(event: Event) {
    this.visibilityChanges.push({
      type: (event.target as Document)?.hidden ? 'hidden' : 'visible',
      timestamp: performance.now(),
    });
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (this.renderModel && !this.suspendRendering) {
      // Check for changes.
      let isChanged = false;
      if (changes.renderModel != null) {
        isChanged = true;
        this.needsCanvasRefresh = true;
      } else if (changes.encoding && !isEqual(changes.encoding.currentValue, changes.encoding.previousValue)) {
        isChanged = true;
        this.needsCanvasRefresh = true;
      } else if (changes.format && !isEqual(changes.format.currentValue, changes.format.previousValue)) {
        isChanged = true;
      } else if (changes.chartType != null) {
        isChanged = true;
      } else if (
        changes.fieldFormats &&
        !isEqual(changes.fieldFormats.currentValue, changes.fieldFormats.previousValue)
      ) {
        isChanged = true;
      } else if (changes.fieldColors && !isEqual(changes.fieldColors.currentValue, changes.fieldColors.previousValue)) {
        isChanged = true;
      } else if (
        changes.facetSizes &&
        !isEqual(changes.facetSizes.currentValue, changes.facetSizes.previousValue) &&
        !this.isMuzeEvent
      ) {
        isChanged = true;
      }
      // Transitioning from suspended rendering -> rendering
      else if (changes.suspendRendering) {
        isChanged = !!changes.suspendRendering.previousValue;
      }

      // Muze renders on facet size changes; this boolean prevents a double rendering.
      this.isMuzeEvent = false;

      // Render if anything changed.
      if (isChanged) {
        this.render();
      }
    }
    // Extra space is needed in designer/exploration contexts when temporal drilling is allowed. This provides
    // space to display the granularity buttons.
    this.isFooterNeeded =
      this.isTemporalDrillAllowed &&
      (this.renderContext === RENDER_TYPE.designer ||
        this.renderContext === RENDER_TYPE.exploration_designer ||
        this.renderContext === RENDER_TYPE.exploration_viewer);
  }

  ngOnDestroy() {
    this.removeResizeObserver();
    this.clearCanvas();
    this.resizeSubscription?.unsubscribe();
    this.resizeSubscription = null;
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.unsubscribeRenderErrors();
  }

  clearCanvas() {
    if (this.canvas?.dispose) {
      MuzeChartMenu.clear(this.canvas);
      // Dispose the canvas
      this.canvas.dispose();
      // Dispose of each of the DataModels including
      // all of the parent data models
      this.createdDataModels.forEach((dm) => {
        while (dm !== undefined) {
          const parent = dm.getParent();
          dm.dispose();
          dm = parent;
        }
      });
      this.createdDataModels = [];

      this.canvas = null;
      // Temp fix for: https://github.com/mode/issues/issues/14840
      // In certain circumstances, Muze orphans a top level canvas element after the canvas has
      // been disposed. This orphaned element continues to take up the same space so next
      // created canvas element is pushed below and offscreen. Until we get a fix for the
      // issue ^^^^ above, we can forcibly remove any orphaned nodes.
      while (this.chartContent?.nativeElement?.hasChildNodes()) {
        this.chartContent.nativeElement.removeChild(this.chartContent.nativeElement.lastChild);
      }
    }
  }

  setCanvas(canvas: MuzeChartRenderer.MuzeCanvas): MuzeChartRenderer.MuzeCanvas {
    this.canvas = canvas;
    this.setResizeObserver();

    Object.values(CANVAS_STATE).forEach((state) => {
      if (state !== CANVAS_STATE.afterRendered) {
        // afterRendered is handled below
        canvas.once(state, () => {
          this.canvasState = state;
          if (this.muzePerformanceStart) {
            this.renderTimings[state] = Math.round(performance.now() - this.muzePerformanceStart);
          }
          if (state === CANVAS_STATE.beforeLayout) {
            // Muze calculates the pagination data before the beforeLayout callback. We process
            // that as soon as possible in case we need to destroy the canvas (due to too much data)
            // before unnecessary work is done in later phases.
            if (this.canvas) {
              this.updatePagination();
            }
          }
        });
      }
    });

    canvas.once('afterRendered', () => {
      this.canvasState = CANVAS_STATE.afterRendered;
      this.unsubscribeRenderErrors();

      let duration = null;
      let options = {};
      if (this.muzePerformanceStart && this.renderPerformanceStart) {
        this.renderTimings[CANVAS_STATE.afterRendered] = Math.round(performance.now() - this.muzePerformanceStart);
        duration = Math.round(performance.now() - this.renderPerformanceStart);
        const markTypes = EncodingHelpers.getMarkTypes(this.encoding).join(' ');
        const tabHiddenDuration = this.calculateTabHiddenDuration();
        options = {
          'mode.webapp_ui.muze.version': this.muzeVersion,
          'mode.webapp_ui.muze.load_data_duration_ms': this.dataStats?.loadDataTime,
          'mode.webapp_ui.muze.before_layout_duration_ms': this.renderTimings[CANVAS_STATE.beforeLayout],
          'mode.webapp_ui.muze.after_rendered_duration_ms': this.renderTimings[CANVAS_STATE.afterRendered],
          'mode.webapp_ui.muze.total_duration_ms': duration,
          'mode.webapp_ui.muze.tab_hidden_duration_ms': tabHiddenDuration,
          'mode.webapp_ui.muze.row_count': this.dataStats?.dataCount,
          'mode.webapp_ui.muze.facet_count': this.dataStats?.facetCount,
          'mode.webapp_ui.muze.element_count': this.dataStats?.elementCount,
          'mode.webapp_ui.muze.is_paging': this.dataStats?.requiresRowPaging,
          'mode.webapp_ui.muze.row_page_size': this.dataStats?.rowPageSize,
          'mode.webapp_ui.muze.encoding.channel': this.encoding.marks.channel,
          'mode.webapp_ui.muze.encoding.mark_types': markTypes,
        };
        const pagingMetadata = this.canvas?.getPaginationMetaInfo();
        if (pagingMetadata != null) {
          options = {
            ...options,
            'mode.webapp_ui.muze.page_count': pagingMetadata.pageCount,
            'mode.webapp_ui.muze.current_page': pagingMetadata.currentPageNumber,
            'mode.webapp_ui.muze.page_facet_count': pagingMetadata.facetCountForCurrentPage,
          };
        }
      }
      this.visibilityChanges = [];
      this.renderEnd.emit({ options });
      const { dataCount, schemaCount, facetCount } = this.dataStats ?? {};
      this.embedAction.emit({
        type: 'MuzeChartRendered', // TODO: taken from legacy EmbedActionType enum
        token: this.token,
        dataCount,
        schemaCount,
        rowCount: canvas.rows().length,
        columnCount: canvas.columns().length,
        facetCount,
        duration,
      });
    });
    return canvas;
  }

  calculateTabHiddenDuration() {
    //
    // Calculate how long the tab was hidden based on the visibility events that were received.
    // We can start rendering either while the tab is visible or while it is hidden. Therefore, the
    // first event could be either a 'hidden' or 'visible' event. We always end the render while
    // the tab is visible so the last event is always a 'visible' event.
    // Examples:
    // 1. Viz starts loading and user tabs away and then back.
    //    Render start: 1000 Events: { hidden at 1200 } { visible at 1450 }
    //    Tab is hidden for 1450 - 1200 = 250 ms.
    // 2. Report starts to load but viz hasn't started rendering yet when user tabs away and then back later.
    //    Render start: 1000 Events: { visible at 1450 }
    //    Tab is hidden for 1450 - 1000 = 450 ms.
    //
    // Since 'visible' is always the last event, we can reduce the array from right to left accumulating
    // the differences between 'visible' and 'hidden'. The special case is when the first element in the
    // array is visible. In that case, we take the difference between 'visible' and the render start time.
    return this.visibilityChanges.reduceRight((hiddenDuration, event, index, changes) => {
      // Skip 'hidden' events
      if (event.type === 'hidden') return hiddenDuration;

      // This is a 'visible' event. Get the event before this one if there is one.
      const prevEvent = index > 0 ? changes[index - 1] : undefined;

      // The previous event should be 'hidden'. If not just bail. This shouldn't happen
      if (prevEvent && prevEvent.type !== 'hidden') return hiddenDuration;

      // Calculate the difference between the 'visible' and 'hidden event,
      // Or, between the 'visible' and start of rendering.
      return prevEvent
        ? hiddenDuration + (event.timestamp - prevEvent.timestamp)
        : hiddenDuration + (event.timestamp - (this.renderPerformanceStart ?? event.timestamp));
    }, 0);
  }

  getCanvas(): MuzeChartRenderer.MuzeCanvas {
    if (this.canvas && !this.needsCanvasRefresh) {
      return this.canvas; // Only ever generate one canvas
    }
    this.clearCanvas();
    return MuzeChartRenderer.getMuzeCanvas(this.canvas);
  }

  registerSubscriptions(canvas: MuzeChartRenderer.MuzeCanvas) {
    const enableDynamicDrilldown =
      this.drillAnywhereEnabled && ChartTypes.isVegasChartType(this.chartType as ChartTypes.ChartType);
    const menuOptions = MuzeChartMenu.options({
      enableExploration: this.enableContextExploration,
      preventExpandingInteraction: this.isEmbedded || this.isWhiteLabel || this.preventExpandingInteraction,
      enableDynamicDrilldown,
      enableTemporalDrill: !enableDynamicDrilldown,
      getIcon: (iconName: string) => this.capraIconsRegistry.getIcon(iconName) ?? '',
    });
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = this.reportApiService?.subscribe(`chart_${this.token}`, (action: InterActions.Union) => {
      switch (action.type) {
        case InterActions.Type.MarkClick:
          if (this.renderContext !== RENDER_TYPE.report_builder) {
            const { clientX, clientY } = action.event;
            // clear context menu if user clicks same mark again
            if (this.currentContextMenuX === clientX && this.currentContextMenuY === clientY) {
              MuzeChartMenu.clear(canvas);
              this.currentContextMenuX = null;
              this.currentContextMenuY = null;
              return;
            } else {
              const dispatch = (item: InterActions.ContextItem) => {
                this.chartAction.emit({
                  componentId: `chart_${this.token}`,
                  action: { item, type: InterActions.Type.ContextClick },
                });
              };

              this.currentContextMenuX = clientX;
              this.currentContextMenuY = clientY;

              // Only show the chart menu if there is something in the menu.
              // Currently, there are two possibilities:
              //  - View Data: Hidden if embedded/WLE
              //  - Drill Anywhere
              const displayMuzeChartMenu: boolean = enableDynamicDrilldown || (!this.isEmbedded && !this.isWhiteLabel);
              if (displayMuzeChartMenu) {
                MuzeChartMenu.show(dispatch, action, canvas, this.renderModel, this.encoding, menuOptions);
              }
            }
          }

          break;
        case InterActions.Type.ChartClick:
          this.currentContextMenuX = null;
          this.currentContextMenuY = null;
          MuzeChartMenu.clear(canvas);
          break;
        case InterActions.Type.ContextClick:
          switch (action.item.type) {
            case InterActions.ContextItemType.ExploreMark: {
              MuzeChartMenu.clear(canvas);

              this.embedAction.emit({
                type: 'ExploreChartMark', // TODO: taken from legacy EmbedActionType enum
                target: action.item.target,
                filters: action.item.filters,
                chartToken: this.token,
              });
              break;
            }
            case InterActions.ContextItemType.Drilldown:
              MuzeChartMenu.clear(canvas);

              this.embedAction.emit({
                type: 'DrillDown', // TODO: taken from legacy EmbedActionType enum
                target: action.item.target,
                chartToken: this.token,
              });
              break;
            case InterActions.ContextItemType.Keep:
            case InterActions.ContextItemType.Exclude:
            case InterActions.ContextItemType.ViewData:
              MuzeChartMenu.clear(canvas);
              break;
            case InterActions.ContextItemType.DynamicDrillDown:
              {
                this.embedAction.emit({
                  type: 'DynamicDrillDown',
                  target: action.item.target,
                  targetFieldId: action.item.targetFieldId,
                  drillToField: action.item.drillToField,
                  dateTruncUnit: action.item.dateTruncUnit,
                  chartToken: this.token,
                });
                MuzeChartMenu.clear(canvas);

                // Track the drilldown in Segment
                this.embedAction.emit({
                  type: 'AnalyticsEvent',
                  name: AnalyticsEvent.Name.UiClicked,
                  location: AnalyticsEvent.SubContext.Chart,
                  action:
                    action.item.dateTruncUnit !== undefined
                      ? `DynamicDrillDown (${action.item.dateTruncUnit})`
                      : 'DynamicDrillDown',
                  context: this.renderContext,
                  context_id: this.token,
                  chartType: this.chartType,
                });
              }
              break;
          }
          break;
      }
    });
  }

  async paginationSetPage(page: number) {
    if (this.canvas) {
      // Set a listener to know when the new page is done rendering
      this.canvas.once('afterRendered', () => {
        this.renderEnd.emit();
        this.updatePagination();
      });
      // Let everyone know that we are starting to render
      this.renderStart.emit(true);

      const pagingMetadata = this.canvas.getPaginationMetaInfo();
      this.embedAction.emit({
        type: 'AnalyticsEvent',
        name: AnalyticsEvent.Name.UiClicked,
        location: 'Facet Pagination',
        action: `Change page from ${pagingMetadata.currentPageNumber} to ${page} of ${pagingMetadata.pageCount}`,
        context: this.renderContext,
        context_id: this.token,
        chartType: this.chartType,
      });
      // Allow the UI to catch up
      await pause();
      // Render the new page
      this.canvas.goToPage(page);
    }
  }

  private updatePagination() {
    if (this.canvas) {
      const paging: Paging = {
        isPaginated: false,
        currentPage: 1,
        totalPageCount: 1,
        totalFacetCount: 1,
        facetCountForPage: 1,
        pageSize: 1,
      };
      if (this.dataStats?.requiresRowPaging) {
        const pagingMetadata = this.canvas.getPaginationMetaInfo();
        if ((pagingMetadata.rowsCountForCurrentPage ?? 0) <= MuzeChartRenderer.PAGING_MAX_ROWS_PER_PAGE) {
          paging.isPaginated = (pagingMetadata?.pageCount ?? 0) > 1;
          paging.currentPage = pagingMetadata?.currentPageNumber ?? 0;
          paging.totalPageCount = pagingMetadata?.pageCount ?? 0;
          paging.totalFacetCount = pagingMetadata?.totalFacetCount ?? 0;
          paging.facetCountForPage = pagingMetadata?.facetCountForCurrentPage ?? 0;
          paging.pageSize = this.canvas.config()?.pagination?.pageSize ?? 1;
        } else {
          // This page is too big to render. Send a DataLimitError and dispose of the canvas
          // to prevent rendering.
          const dataLimitError = {
            stats: this.dataStats,
            reason: ChartDataErrorReason.TooManyPageRows,
            limit: MuzeChartRenderer.PAGING_MAX_ROWS_PER_PAGE,
            actual: pagingMetadata.rowsCountForCurrentPage ?? 0,
            rowPagingEnabled: true,
            rowPageSize: pagingMetadata.rowsCountForCurrentPage ?? 0,
          };
          notifyMuzeRenderError(
            this.errorService,
            VizRendererHelpers.toChartDataError(dataLimitError),
            this.canvas,
            this.muzeVersion,
            this.encoding,
            this.chartType,
            this.renderContext,
            this.canvasState
          );
          this.dataLimitsExceeded.emit(dataLimitError);
          this.clearCanvas();
          this.needsCanvasRefresh = false;
        }
      }
      // Update the paging observable
      this.paging$.next(paging);
    }
  }

  setResizeObserver() {
    this.removeResizeObserver();
    this.chartContentResizeObserver = new ResizeObserver(() => {
      this.resizeSubject.next();
    });
    this.chartContentResizeObserver.observe(this.chartContent.nativeElement);
  }

  removeResizeObserver() {
    if (this.chartContentResizeObserver) {
      this.chartContentResizeObserver.disconnect();
    }
    this.chartContentResizeObserver = undefined;
  }

  unsubscribeRenderErrors() {
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
    this.errorSubscription = null;
  }

  checkDataLimits(dataStats: MuzeChartRenderer.VisualizationDataStats) {
    this.dataStats = dataStats;

    // Check for any MAX data limits exceeded
    const maxLimitExceeded = VizRendererHelpers.checkDataLimits(this.dataStats, {
      facets: MuzeChartRenderer.MAX_FACETS,
      points: MuzeChartRenderer.MAX_POINTS,
      series: MuzeChartRenderer.MAX_SERIES,
      elements: this.maxElementCount,
    });
    if (maxLimitExceeded !== undefined) {
      return {
        ...maxLimitExceeded,
        stats: this.dataStats,
      };
    }

    // Check for large limits
    const delayLimitExceeded = VizRendererHelpers.checkDataLimits(this.dataStats, {
      facets: RENDER_DELAY_FACETS,
      points: RENDER_DELAY_POINTS,
      series: RENDER_DELAY_SERIES,
      elements: this.maxElementCount / 10,
    });
    if (delayLimitExceeded !== undefined) {
      this.renderDelay.emit();
    }
  }

  resize() {
    if (this.canvas) {
      this.canvas
        .height(this.chartContent.nativeElement.offsetHeight)
        .width(this.chartContent.nativeElement.offsetWidth);
    }
  }

  async render() {
    // Make sure we cleared the old error subscription
    this.unsubscribeRenderErrors();

    this.renderPerformanceStart = performance.now();
    this.visibilityChanges = [];
    // Muze shows error for empty charts, so cleanup and return
    if (this.renderModel.isEmpty()) {
      this.clearCanvas();
      this.subscription?.unsubscribe();
      this.subscription = null;
      this.renderEnd.emit({ error: new NoDataError() }); // emitter needed in order to hide the fetching data animation
      return;
    }
    // Emit a renderStart event. Boolean parameter indicates whether this is a new canvas
    // or a reuse of the existing canvas.
    this.renderStart.emit(this.canvas == null);
    const format = this.format || DEFAULT_CHART_FORMAT;
    const palette: readonly string[] = computePalette(format);
    const eventHandler = (action: InterActions.Union) => {
      this.chartAction.emit({
        componentId: `chart_${this.token}`,
        action,
      });
    };

    const updateFacetSizesFunc = (event: FacetSerializer) => {
      this.isMuzeEvent = true;
      this.updateFacetSizes.emit(event);
    };

    const handleMuzeRenderError = (error: any) => {
      this.unsubscribeRenderErrors();
      this.canvas = null; // clear the canvas so it can be reinitialized
      this.renderEnd.emit({ error });
      console.error(error);
      notifyMuzeRenderError(
        this.errorService,
        error,
        this.canvas,
        this.muzeVersion,
        this.encoding,
        this.chartType,
        this.renderContext,
        this.canvasState
      );
    };

    // Set any required feature flags prior to building the muze configuration. Ex:
    // MuzeFeatureFlags[FeatureFlag.MyFeatureFlag] = this.isMyFeatureFlagEnabled;
    MuzeFeatureFlags[FeatureFlag.VizExplorerMaxFacetsPerPage] = this.maxFacetPageSize;

    // Clear the render timings
    this.renderTimings = {};

    // Construct the Muze data and schema
    const dataResults = MuzeChartRenderer.buildMuzeDataAndSchema(
      this.renderModel,
      this.encoding,
      format,
      this.fieldFormats
    );
    this.muzeVersion = dataResults.muzeVersion;
    this.renderTimings['datamodelLoaded'] = dataResults.dataStats.loadDataTime ?? -1;

    if ((dataResults.dataModel.getData()?.data?.length ?? 0) === 0) {
      this.clearCanvas();
      this.unsubscribeRenderErrors();
      this.subscription?.unsubscribe();
      this.subscription = null;
      this.needsCanvasRefresh = false;
      this.renderEnd.emit({ error: new NoDataError() });
      return;
    }
    // Validate that the data doesn't exceed our rendering limits
    const dataLimitError = this.checkDataLimits(dataResults.dataStats);
    if (dataLimitError !== undefined) {
      notifyMuzeRenderError(
        this.errorService,
        VizRendererHelpers.toChartDataError(dataLimitError),
        this.canvas,
        this.muzeVersion,
        this.encoding,
        this.chartType,
        this.renderContext,
        this.canvasState
      );
      this.dataLimitsExceeded.emit(dataLimitError);
      this.canvas = null;
      this.needsCanvasRefresh = false;
      return;
    }
    // We need to wait so that the loading screen can update
    await pause();

    // Subscribe to any errors that might occur between now and when
    // the canvas has completed rendering.
    this.errorSubscription = this.ngZone.onError.subscribe((err) => {
      const error = err?.rejection ? err.rejection : err;

      handleMuzeRenderError(error);
    });

    this.muzePerformanceStart = performance.now();

    try {
      const canvas = this.getCanvas();

      // Due to the async nature of this function, the render model
      // could be destroyed before we get to the rendering step
      // even though we checked it before calling render().
      if (this.renderModel == null) {
        this.clearCanvas();
        this.unsubscribeRenderErrors();
        this.subscription?.unsubscribe();
        this.subscription = null;
        this.needsCanvasRefresh = false;
        this.renderEnd.emit();
        return;
      }

      // Keep track of any data models we create. Not sure why Muze can't clean these
      // up when we dispose of the canvas.
      this.createdDataModels = [dataResults.dataModel];

      MuzeChartRenderer.renderMuzeChart(
        this.renderModel,
        this.encoding,
        dataResults,
        format,
        this.fieldFormats,
        this.fieldColors,
        palette,
        this.facetSizes,
        this.chartContent.nativeElement,
        this.token,
        canvas,
        eventHandler,
        this.renderContext === RENDER_TYPE.designer || this.renderContext === RENDER_TYPE.exploration_designer
          ? updateFacetSizesFunc
          : undefined
      );
      // Track the totals data models too. We will need to keep this updated if
      // additional data models are created.
      this.createdDataModels.push(canvas.config()?.columns?.totals?.data?.dataModel);
      this.createdDataModels.push(canvas.config()?.rows?.totals?.data?.dataModel);
      this.setCanvas(canvas);
      this.registerSubscriptions(canvas);
    } catch (err) {
      handleMuzeRenderError(err);
    } finally {
      this.needsCanvasRefresh = false;
    }
  }
}
